import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import ClassIcon from '../icons/ClassIcon';
import SchoolIcon from '../icons/SchoolIcon';
import PositionIcon from '../icons/PositionIcon';
import LocationIcon from '../icons/LocationIcon';
import RulerVerticalIcon from '../icons/RulerVerticalIcon';
import ScaleIcon from '../icons/ScaleIcon';
import AllConferenceIcon from '../icons/AllConferenceIcon';
import AllAmericanIcon from '../icons/AllAmericanIcon';
import WatchListIcon from '../icons/WatchListIcon';
import LinkIcon from '../icons/LinkIcon';
import PffLinkIcon from '../icons/PffLinkIcon';
import TeamsIcon from '../icons/TeamsIcon';
import TransferPortalProfileIcon from '../icons/TransferPortalProfileIcon';
import UserIcon from '../icons/UserIcon';
import TeamLogo from '../atoms/TeamLogo';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import Links from '../molecules/Links';
import ShowMoreDots from '../molecules/ShowMoreDots';
import WatchList from '../molecules/WatchList';
import { inchesToFeetAndInches, datetimeToDate } from '../services/converter';
import { isValidDOB, capitalizeFirstLetter, prepareLinks } from '../services/player';
import {
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_DARK_GRAY,
  COLOR_DARK_GREEN,
  COLOR_MIDDLE_GRAY,
} from '../styles/colors';
import Player from '../types/Player';
import Position from '../types/Position';
import College from '../types/College';
import HighSchool from '../types/HighSchool';
import User from '../types/User';
import { LinkType } from '../types/Link';
import { Typography } from '@material-ui/core';
import OurladsIcon from '../icons/OurladsIcon';
import On3Icon from '../icons/On3Icon';
import { PlayerNILValues } from '../types/PlayerNILValues';
import NilValuesTooltip from '../molecules/NilValuesTooltip';
import { getSingleServeUrl } from '../services/ssa-auth';
import Spinner from '../atoms/Spinner';

interface PlayerProfileCollegeOverviewProps {
  className?: string;
  player?: Player;
  selectedPosition?: Position;
  user: User | undefined;
  playerNilValues?: PlayerNILValues[];
  playerOn3Date?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  playerProfileCollegeOverview: {},

  divider: {
    width: '1px',
    height: '30px',
    margin: theme.spacing(0, 2.5),
    background: COLOR_MIDDLE_GRAY,
  },

  links: {
    // padding: theme.spacing(1, 0),
    height: 'auto',
    alignItems: 'center',
    overflow: 'visible',
  },

  teamLogo: {
    height: '32px',
    marginRight: theme.spacing(2),
  },
  link: {
    color: COLOR_BLUE,
    textDecoration: 'underline',
    cursor: 'pointer',

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },

  collegeStatus: {
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    lineHeight: 1,
  },
  active: {
    color: COLOR_DARK_GREEN,
  },
  former: {
    color: COLOR_DARK_GRAY,
  },

  more: {
    marginLeft: theme.spacing(2),
  },

  formerColleges: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    listStyle: 'none',
    padding: 0,
  },

  formerCollege: {},

  sportSourceLogo: {
    width: 26,
    height: 'auto',
    marginTop: 6,
  },

  userIcon: {
    height: 22,
  },
  transferYear: {
    marginLeft: '5px'
  },
  positionText: {
    fontWeight: 600,
    marginRight: '8px',
  },
  positionCode: {
    marginRight: '5px'
  },
  depthChartContent: {
    alignItems: 'start',
    flexDirection: 'column',
  },
  ourladsIcon: {
    height: '55px',
    width: '55px',
  },
  iconCell: {
    height: 'auto',
    textAlign: 'center',
    width: '55px',
  },
  listIcon: {
    margin: '0 auto',
  },
  on3Icon: {
    height: 'auto',
    margin: '0 auto',
    width: 30,
  },
  leftMargin: {
    marginLeft: '10px',
  },
}), { name: PlayerProfileCollegeOverview.name });

export default function PlayerProfileCollegeOverview (props: PlayerProfileCollegeOverviewProps) {
  const {
    className,
    player,
    selectedPosition,
    user,
    playerNilValues=[],
    playerOn3Date=''
  } = props;
  const classes = useStyles();
  const [ssaLoading, setSsaLoading] = useState<boolean>(false);

  const primaryCollege = (player?.playerColleges || [])
    .find((college:College) => !!college?.isPrimary);
  const formerColleges = (player?.playerColleges || [])
    .filter((college:College) => !!college?.isFormer)
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  const highSchool = (player?.highSchools || [])
    .find((highSchool:HighSchool) => highSchool && highSchool.isPrimary);
  const team = primaryCollege?.team;

  const rosterHeight = primaryCollege?.height || highSchool?.height || null;
  const rosterWeight = primaryCollege?.weight || highSchool?.weight || null;
  const dob = !!player?.dob && datetimeToDate(player.dob);
  const jerseyNumber = primaryCollege?.jerseyNumber;
  const transferStatus = player?.transferStatus;
  const links = prepareLinks(player);
  const ssaLink = (links?.find(link => link.key === LinkType.ssa) || {})?.value;
  const depthChartData = player?.depthChart ? player?.depthChart.split('/').map(item => item.trim()).filter(Boolean): [];
  const on3PublicNILVal = player?.on3PublicNILValuation || null;

  function onSsaLinkClick() {
    setSsaLoading(true); // Show spinner
    getSingleServeUrl(null, ssaLink).then((link:string) => {
      window.location.href = link;
    }).finally(() => setSsaLoading(false)); // Hide spinner
  }

  return (
    <NamedBox
      className={clsx(classes.playerProfileCollegeOverview, className)}
      name='Overview'
    >
      <ListItem
        icon={ClassIcon}
        iconCellClassName={classes.iconCell}
        iconClassName={classes.listIcon}
        title='Recruiting Class:'
      >
        <div><b>{primaryCollege?.recruitingClass || highSchool?.graduatingClass || 0}</b></div>

        {dob && isValidDOB(dob) && (
          <>
            <div className={classes.divider} />
            <div>DOB: <b>{dob}</b></div>
          </>
        )}
      </ListItem>

      {selectedPosition && (
        <ListItem
          icon={PositionIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='Position:'
        >
          <div><b>{selectedPosition}</b></div>
        </ListItem>
      )}

      {(player && team?.name) && (
        <ListItem
          icon={SchoolIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='College:'
        >
          <TeamLogo
            className={classes.teamLogo}
            team={team}
          />

          {user?.accessLevel?.accessCollegeTeamPage
            ? <Link
                to={`/team/${team.slug}`}
                className={classes.link}
              >
                <b>{team.name}</b>
              </Link>
            : <b>{team.name}</b>
          }

          {(user?.accessLevel?.accessCollegeTeamPage && primaryCollege) && (
            <span
              className={clsx(
                classes.collegeStatus,
                primaryCollege?.isFormer ? classes.former : classes.active,
              )}
            >
              {primaryCollege?.isFormer ? '(Former)' : '(Active)'}
            </span>
          )}

          {!!formerColleges.length && (
            <ShowMoreDots
              className={classes.more}
              tooltipContent={(
                <ul className={classes.formerColleges}>
                  {formerColleges.map(college => (
                    <li
                      key={college.id}
                      className={classes.formerCollege}
                    >
                      {college?.team?.name}
                    </li>
                  ))}
                </ul>
              )}
            />
          )}
        </ListItem>
      )}

      {(team?.city || team?.state) && (
        <ListItem
          icon={LocationIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='College Location:'
        >
          <b>{team?.city || ''}{(team?.city && team?.state) ? `, ${team.state}` : (!team?.city && team?.state) ? team?.state : ''}</b>
        </ListItem>
      )}

      {!!rosterHeight && (
        <ListItem
          icon={RulerVerticalIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='Roster Height:'
        >
          <b>{inchesToFeetAndInches(rosterHeight)}</b>
        </ListItem>
      )}

      {!!rosterWeight && (
        <ListItem
          icon={ScaleIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='Roster Weight:'
        >
          <b>{rosterWeight} lbs.</b>
        </ListItem>
      )}

      {!!jerseyNumber && (
        <ListItem
          icon={TeamsIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='Jersey Number:'
        >
          <b>{jerseyNumber}</b>
        </ListItem>
      )}

        <ListItem
          icon={TransferPortalProfileIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='Transfer Portal:'
        >
          <b className={transferStatus == 'active' ? classes.active : ''}>{capitalizeFirstLetter(transferStatus || '') || "N/A"}</b>
          <b className={classes.transferYear}>{player?.transferYear}</b>
        </ListItem>

      {primaryCollege?.url && (
        <ListItem
          icon={UserIcon}
          iconClassName={clsx(classes.userIcon, classes.listIcon)}
          iconCellClassName={classes.iconCell}
          title='College Roster:'
        >
          <b>
            <a
              className={classes.link}
              href={primaryCollege?.url}
              target='_blank'
            >
              Roster Bio
            </a>
          </b>
        </ListItem>
      )}

      {!!ssaLink && (
        <ListItem
          icon={() => <img className={clsx(classes.sportSourceLogo)} src='/images/websites/ssa.png' />}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='SportSource:'
        >
          <b>
            <a
              className={classes.link}
              onClick={() => onSsaLinkClick()}
            >
              SSA Player Link
            </a>
          </b>
          {<Spinner inProgress={ssaLoading} />} {/* Spinner here */}
        </ListItem>
      )}


      {!!links?.length && (links.find(link => link.key === LinkType.pff) || {}).value && (
        <ListItem
          contentClassName={classes.links}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          className={classes.links}
          icon={PffLinkIcon}
          title='Pro Football Focus:'
        >
          <a
            className={classes.link}
            href={`${(links.find(link => link.key === LinkType.pff) || {}).value}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            <b>PFF Ultimate Link</b>
          </a>
        </ListItem>
      )}

      {!!player?.allConference?.length && (
        <ListItem
          icon={AllConferenceIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='All-Conference:'
        >
          <b>{player.allConference.join(', ')}</b>
        </ListItem>
      )}

      {!!player?.allAmerican?.length && (
        <ListItem
          icon={AllAmericanIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='All-American:'
        >
          <b>{player.allAmerican.join(', ')}</b>
        </ListItem>
      )}

      {!!player?.watchList?.length && (
        <ListItem
          icon={WatchListIcon}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          title='Watch List:'
        >
          <WatchList list={player.watchList} />
        </ListItem>
      )}

      {!!depthChartData.length && (
        <ListItem
          icon={OurladsIcon}
          iconCellClassName={classes.iconCell}
          title='Depth Chart:'
          iconClassName={clsx(classes.ourladsIcon, classes.listIcon)}
          contentClassName={classes.depthChartContent}
        >
          <Typography className={classes.positionText}>
            {depthChartData.map((data, index) => (
              <span key={index} className={classes.positionCode}>{data}</span>
            ))}
            {player?.ourladsUpdatedDate ? `Updated ${datetimeToDate(player?.ourladsUpdatedDate)}` : ""}
          </Typography>
        </ListItem>
      )}

      {on3PublicNILVal && <ListItem
        icon={() => <On3Icon className={classes.on3Icon} width={2000} viewBox={"0 0 2500 2500"} />}
        iconCellClassName={classes.iconCell}
        iconClassName={classes.listIcon}
        title='Total Player Value:'
      >
        <b>${on3PublicNILVal}</b>
        <NilValuesTooltip playerNilValues={playerNilValues} />
        <b className={classes.leftMargin}>{playerOn3Date && datetimeToDate(playerOn3Date)}</b>
      </ListItem>}

      {!!links?.length && (
        <ListItem
          contentClassName={classes.links}
          iconCellClassName={classes.iconCell}
          iconClassName={classes.listIcon}
          icon={LinkIcon}
          title='Links:'
        >
          <Links links={links} />
        </ListItem>
      )}
    </NamedBox>
  );
}
